import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { callBackendFunction } from '@/services/paymentService';
import type {
  AdminUserMutationResult,
  AdminUserSummary,
  ArchiveAdminUserPayload,
  CreateAdminUserPayload,
  PasswordResetAdminUserPayload,
  ToggleAdminUserStatePayload,
  UpdateAdminUserPayload,
} from '../types/adminUsers';

const USERS_COLLECTION = 'users';

type FirestoreTimestampLike = {
  toDate?: () => Date;
  seconds?: number;
};

type FirestoreUserDoc = {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  email?: string | null;
  role?: AdminUserSummary['role'];
  phone?: string | null;
  internalNotes?: string | null;
  active?: boolean;
  actif?: boolean;
  deleted?: boolean;
  archived?: boolean;
  createdAt?: FirestoreTimestampLike | Date | string | null;
  updatedAt?: FirestoreTimestampLike | Date | string | null;
  lastLoginAt?: FirestoreTimestampLike | Date | string | null;
  lastPasswordResetEmailAt?: FirestoreTimestampLike | Date | string | null;
  lastEmailSentAt?: FirestoreTimestampLike | Date | string | null;
};

const normalizeString = (value: unknown): string => String(value ?? '').trim();

const toDate = (value: FirestoreUserDoc[keyof FirestoreUserDoc]): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'object' && typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (typeof value === 'object' && typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }

  return null;
};

const buildFullName = (firstName: string, lastName: string, fallbackFullName?: string | null): string => {
  const joined = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (joined) {
    return joined;
  }

  const fallback = normalizeString(fallbackFullName);
  return fallback || 'Utilisateur sans nom';
};

const mapUser = (uid: string, data: FirestoreUserDoc): AdminUserSummary => {
  const firstName = normalizeString(data.firstName);
  const lastName = normalizeString(data.lastName);
  const fullName = buildFullName(firstName, lastName, data.fullName);
  const email = normalizeString(data.email);
  const deleted = data.deleted === true || data.archived === true;
  const active = !deleted && data.active !== false && data.actif !== false;

  return {
    uid,
    firstName,
    lastName,
    fullName,
    email,
    role: data.role || 'User',
    phone: normalizeString(data.phone),
    internalNotes: normalizeString(data.internalNotes),
    active,
    deleted,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    lastLoginAt: toDate(data.lastLoginAt),
    lastPasswordResetEmailAt: toDate(data.lastPasswordResetEmailAt),
    lastEmailSentAt: toDate(data.lastEmailSentAt),
  };
};

export async function listAdminUsers(): Promise<AdminUserSummary[]> {
  const snapshot = await getDocs(collection(db, USERS_COLLECTION));
  return snapshot.docs
    .map((document) => mapUser(document.id, document.data() as FirestoreUserDoc))
    .sort((left, right) => (right.createdAt?.getTime() ?? 0) - (left.createdAt?.getTime() ?? 0));
}

export async function createAdminUser(payload: CreateAdminUserPayload): Promise<AdminUserMutationResult> {
  return callBackendFunction<CreateAdminUserPayload, AdminUserMutationResult>('createUserAsAdmin', payload);
}

export async function updateAdminUser(payload: UpdateAdminUserPayload): Promise<AdminUserMutationResult> {
  return callBackendFunction<UpdateAdminUserPayload, AdminUserMutationResult>('updateUserAsAdmin', payload);
}

export async function setAdminUserActiveState(payload: ToggleAdminUserStatePayload): Promise<AdminUserMutationResult> {
  return callBackendFunction<ToggleAdminUserStatePayload, AdminUserMutationResult>('setUserActiveState', payload);
}

export async function setAdminUserArchivedState(payload: ArchiveAdminUserPayload): Promise<AdminUserMutationResult> {
  return callBackendFunction<ArchiveAdminUserPayload, AdminUserMutationResult>('setUserArchivedState', payload);
}

export async function sendAdminUserPasswordReset(payload: PasswordResetAdminUserPayload): Promise<AdminUserMutationResult> {
  return callBackendFunction<PasswordResetAdminUserPayload, AdminUserMutationResult>('sendPasswordResetAsAdmin', payload);
}